import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import { FormattedMessage, useIntl } from 'react-intl'
import cn from 'classnames'

import type { validateEmail, validatePassword } from 'client/helpers/Form'

import GoogleLoginButton from '../GoogleLoginButton'
import { PasswordInput } from '../Atom/Inputs/Input/Password'
import { getMessage } from '../LoginForm/messages'
import { ButtonPrimary } from '../Button/styles'
import Input from '../Atom/Inputs/Input/Input'

import styles from './styles.module.css'
import MessageTerms from 'client/components/MessageTerms'
import { getCurrentUrl } from '../LoginForm'
import { useBrowser } from '@s4l/utils/hooks/useBrowser'

type ErrorsType = {
  email: ReturnType<typeof validateEmail>
  password: ReturnType<typeof validatePassword>
}

interface ILoginAndSignUpFrom {
  title: string | null
  text: string | React.ReactNode[]
  errors: ErrorsType
  setEmail: (value: string) => void
  setPassword: (value: string) => void
  email: string
  password: string
  handleSubmitForm: (event: React.FormEvent) => void
  isSignUp?: boolean
  showTerms?: boolean
  from?: string
}
export function LoginAndSignUpForm(props: Readonly<ILoginAndSignUpFrom>) {
  const intl = useIntl()
  const { isIOSApp } = useBrowser()

  const {
    title,
    text,
    errors,
    setEmail,
    setPassword,
    email,
    password,
    handleSubmitForm,
    isSignUp = false,
    showTerms = false,
    from,
  } = props

  const [signUpUrl, setSignUpUrl] = useState<string>('')

  useEffect(() => {
    const encodedURL = getCurrentUrl(window)
    const signUpURL = `/signup?referrer=${encodedURL}`
    setSignUpUrl(signUpURL)
  }, [])

  return (
    <>
      {title && (
        <header>
          <h1 className={styles.title}>{title}</h1>
        </header>
      )}
      <div className={styles.container}>
        <div>
          <p className={cn(styles.text, { 'mb-16': isIOSApp })}> {text} </p>
          {!isIOSApp && (
            <>
              <div data-testid="google-login-btn" className={styles.social}>
                <GoogleLoginButton from={from} />
              </div>
              <div className={styles.separator}>
                <span className={styles.separatorText}>
                  {intl.formatMessage({ id: 'global.or' })}
                </span>
              </div>
            </>
          )}
          <form data-testid="form">
            <div className={styles.fieldset}>
              <div className={styles.field}>
                <Input
                  errors={errors.email
                    ?.slice(0, 1)
                    ?.map(error => intl.formatMessage({ id: getMessage(error.id) }))}
                  label={intl.formatMessage({ id: 'global.email' })}
                  name="email"
                  onChange={setEmail}
                  type="email"
                  value={email}
                  dataTestId="email-field"
                />
              </div>

              <div className={styles.field}>
                <PasswordInput
                  errors={errors.password
                    ?.slice(0, 1)
                    ?.map(error => intl.formatMessage({ id: getMessage(error.id) }))}
                  label={intl.formatMessage({ id: 'global.password' })}
                  onChange={setPassword}
                  value={password}
                />
              </div>

              <div className={styles.buttonContainer}>
                <ButtonPrimary onClick={handleSubmitForm}>
                  {intl.formatMessage({ id: isSignUp ? 'global.create-account' : 'global.login' })}
                </ButtonPrimary>
              </div>
              {!isSignUp && (
                <Link href="/forgot-password" passHref>
                  <a
                    className={cn(styles.link, styles.forgotPasswordLink)}
                    title={intl.formatMessage({ id: 'login.forgot-password' })}
                  >
                    {intl.formatMessage({ id: 'login.forgot-password' })}
                  </a>
                </Link>
              )}
            </div>
          </form>

          {!isSignUp ? (
            <p>
              {intl.formatMessage({ id: 'login.already-account' })}{' '}
              <Link href={signUpUrl} passHref>
                <a
                  className={styles.link}
                  title={intl.formatMessage({ id: 'global.create-account' })}
                >
                  {intl.formatMessage({ id: 'global.create-account' })}
                </a>
              </Link>
            </p>
          ) : (
            <p>
              <FormattedMessage id="login.not-account" />
              &nbsp;
              <Link href="/login" passHref>
                <a
                  className={styles.link}
                  rel="noopener nofollow"
                  title={intl.formatMessage({ id: 'login.not-account' })}
                  aria-label={intl.formatMessage({ id: 'login.not-account' })}
                >
                  {intl.formatMessage({ id: 'global.login' })}
                </a>
              </Link>
            </p>
          )}
          {showTerms && (
            <div className={styles.termsContainer}>
              <MessageTerms />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
